import { Extension } from "@codemirror/state"
import { keymap, ViewUpdate } from "@codemirror/view"
import { useMemo } from "react"
import { clipboardHandler, codeEditorTheme, editorKeymap, keyboardHandler, tooltipToBody } from "../extensions"
import { indentationMarkers } from "@replit/codemirror-indentation-markers"
import { createFormatExtension } from "../extensions/format"
import type { CursorPosition } from "../code-editor"
import { useBaseExtensions } from "./use-base-extensions"

type Props = {
  customExtensions?: Extension[]
  lineWrapping?: boolean
  onChange?: (value: string, viewUpdate: ViewUpdate) => void
  onCursorChange?: (position: CursorPosition) => void
  onEditorUpdate: (update: ViewUpdate) => void
  onFormat?: (formattedCode: string) => void
  onSelectionChange?: (characterCount: number) => void
  readonly?: boolean
  rows?: number
}

export function useExtensions(props: Props) {
  const { readonly, lineWrapping = true, customExtensions = [], rows, onChange, onEditorUpdate, onFormat, onCursorChange, onSelectionChange } = props

  // 使用基础扩展 hook
  const baseExtensions = useBaseExtensions({
    readonly,
    lineWrapping,
    onChange,
    onCursorChange,
    onSelectionChange,
    onEditorUpdate,
    theme: codeEditorTheme({
      isReadOnly: readonly,
      maxHeight: "100%",
      minHeight: "20vh",
      rows,
    }),
    includeKeymap: true,
  })

  const extensions: Extension[] = useMemo(() => {
    const allExtensions: Extension[] = [
      ...baseExtensions,
      tooltipToBody,
      clipboardHandler,
      keyboardHandler, 
    ]

    // 只读模式不需要这些扩展
    if (!readonly) {
      allExtensions.push(
        // 缩进标记
        indentationMarkers({
          highlightActiveBlock: true,
          markerType: "fullScope",
          colors: {
            activeDark: "var(--color-code-indentation-marker-active)",
            activeLight: "var(--color-code-indentation-marker-active)",
            dark: "var(--color-code-indentation-marker)",
            light: "var(--color-code-indentation-marker)",
          },
        }),
        // 编辑器键盘映射
        keymap.of(editorKeymap),
      )
    }

    // 格式化扩展
    const formatExtension = onFormat ? createFormatExtension(onFormat) : []

    return [...allExtensions, formatExtension, ...customExtensions]
  }, [baseExtensions, customExtensions, onFormat, readonly])

  return extensions
}
